/**
 * Loads the remote registry in the browser and registers its remotes with the Module
 * Federation runtime.
 *
 * The build plugin still creates the federation instance — with `remotes: {}` and the
 * full `shared` config — and `registerRemotes` / `loadRemote` from
 * `@module-federation/enhanced/runtime` operate on that same instance. So a remote
 * registered here resolves react, react-dom and the next/* shims from the shell's share
 * scope exactly as the build-time remotes did.
 *
 * `/remotes.json` is same-origin: served by Express in dev and on the current EKS image,
 * uploaded next to index.html on CloudFront (see scripts/registry.ts).
 */

import { registerRemotes } from '@module-federation/enhanced/runtime';
import { reportError } from '@/observability/clientLogger';
import { validateRegistry, type Registry, type RegistryRoute } from './registrySchema';

const REGISTRY_URL = '/remotes.json';

export interface LoadedRegistry extends Registry {
  /** Registry route for a pathname, or undefined. */
  find(pathname: string): RegistryRoute | undefined;
}

let pending: Promise<LoadedRegistry> | null = null;

/** Trailing slash removed and lower-cased — React Router's own matching is case-insensitive. */
function routeKey(pathname: string): string {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return trimmed.toLowerCase();
}

function isLoopbackPage(): boolean {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

async function fetchRegistry(): Promise<LoadedRegistry> {
  // `no-cache` is revalidate, not "don't cache": the ETag makes a repeat load a 304, and a
  // registry edit is picked up on the very next page load rather than after a TTL.
  const response = await fetch(REGISTRY_URL, { cache: 'no-cache', credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`${REGISTRY_URL} returned ${response.status}`);
  }
  const doc: unknown = await response.json();

  // Loopback remotes only when the shell itself is on loopback. A production page never
  // loads code from the visitor's own machine, whatever the registry says.
  const { registry, problems } = validateRegistry(doc, { allowLoopback: isLoopbackPage() });

  // Dropped, not fatal. One bad entry costs one app, not all of them — the CLI and the
  // server's startup check are where a bad file is refused outright.
  for (const problem of problems) {
    reportError(new Error(`Remote registry entry ignored: ${problem}`), {
      event_detail: 'remote_registry_invalid_entry',
      app: 'shell-app',
    });
  }

  registerRemotes(registry.remotes.map(({ name, entry }) => ({ name, entry })));

  const byPath = new Map(registry.routes.map((route) => [routeKey(route.path), route]));
  return { ...registry, find: (pathname) => byPath.get(routeKey(pathname)) };
}

/**
 * The registry, fetched once per page load and shared by every caller.
 *
 * A failure clears the memo so the next call fetches again — a transient network error
 * should not pin the tab to "apps unavailable" until a full reload.
 */
export function loadRemoteRegistry(): Promise<LoadedRegistry> {
  if (!pending) {
    pending = fetchRegistry().catch((error: unknown) => {
      pending = null;
      throw error;
    });
  }
  return pending;
}
